import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { groupBy, orderBy } from 'lodash';
import theme from './BuyerOptionPresetMatrix.module.scss';
import { Banner, LoadingIndicator, Select, StickyContext, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { Button, Card, Checkbox, PopupBar, Radio } from '@ygtechnologies/rubus-ui';
import {
    OptionPresetGroupDto,
    OptionPresetGroupType,
    PresetItemsChangeSet,
    useOptionPresetMatrix,
    useSaveOptionPresetMatrix,
} from '@/services/option-preset/option-preset-group';
import { PresetItemUpdateType } from '@/services/option-preset/option-preset-item';
import { FilterOptionOption } from 'react-select';
import {
    useReactTable,
    getCoreRowModel,
    getExpandedRowModel,
    ColumnDef,
    flexRender,
    ExpandedState,
    CellContext,
} from '@tanstack/react-table';
import { useQueries } from '@tanstack/react-query';
import { BaseProduct, LinkingType, ProductSourceCatalog, ProjectTagDto, RoomDto } from '@/types';
import Tags from '@/components/Tags';
import { OptionGroupDropdownItem } from '@/seller/components/buyer-options/Dependencies/DependenciesEdit/DependenciesEdit';
import { Trans } from '@lingui/react/macro';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { t } from '@lingui/core/macro';
import { CatalogProductThumbnails } from '../../products/ProductThumbnails';
import { ProductMoreInfoModal } from '@/components/OptionConfigurator/ProductMoreInfoModal';
import { RoomPillList } from '../../options/Rooms';
import OptionGroupLabel from '@/seller/components/options/OptionGroupLabel/OptionGroupLabel';
import { useRoomMap } from '@/services/rooms';
import { getOptionItemsByGroup } from '@/services/options/option-items';
import RouterPrompt from '@/components/RouterPrompt/RouterPrompt';

// The editable linkage is held locally as a map keyed by `${presetId}:${optionItemId}`. Presence means the
// product is linked to that style; the boolean value is whether it is the default for its group in that style.
type LinkMap = Map<string, boolean>;

function linkKey(presetId: number, optionItemId: number) {
    return `${presetId}:${optionItemId}`;
}

function linksEqual(a: LinkMap, b: LinkMap) {
    if (a.size !== b.size) return false;
    for (const [key, value] of a) {
        if (!b.has(key) || b.get(key) !== value) return false;
    }
    return true;
}

// Mirror the backend's EnforceSingleDefaultPerGroup so the auto-selected default shows immediately,
// before saving: a group with any linked products always has exactly one default, falling back to the
// first product (lowest sort order) when none is explicitly chosen. `orderedGroupItemIds` must be in
// sort order (it is: the matrix items come back ordered by SortOrder).
function ensureSingleDefault(links: LinkMap, presetId: number, orderedGroupItemIds: number[]) {
    const linked = orderedGroupItemIds.filter((id) => links.has(linkKey(presetId, id)));
    if (linked.length === 0) return;
    const defaultCount = linked.filter((id) => links.get(linkKey(presetId, id)) === true).length;
    if (defaultCount === 1) return;
    linked.forEach((id, index) => links.set(linkKey(presetId, id), index === 0));
}

// Diff the editable draft against the server baseline into per-style Linked / Unlinked / Default deltas.
// Drives both the dirty flag and the save payload from one place so they can never disagree.
function computeChanges(draft: LinkMap, original: LinkMap): PresetItemsChangeSet[] {
    const byPreset = new Map<number, { optionItemId: number; updateType: PresetItemUpdateType }[]>();
    const add = (presetId: number, optionItemId: number, updateType: PresetItemUpdateType) => {
        const list = byPreset.get(presetId);
        if (list) list.push({ optionItemId, updateType });
        else byPreset.set(presetId, [{ optionItemId, updateType }]);
    };

    for (const key of new Set<string>([...draft.keys(), ...original.keys()])) {
        const [presetIdStr, optionItemIdStr] = key.split(':');
        const presetId = Number(presetIdStr);
        const optionItemId = Number(optionItemIdStr);
        const inDraft = draft.has(key);
        const inOriginal = original.has(key);
        const draftDefault = draft.get(key) === true;
        const originalDefault = original.get(key) === true;

        if (inDraft && !inOriginal) {
            add(presetId, optionItemId, draftDefault ? PresetItemUpdateType.Default : PresetItemUpdateType.Linked);
        } else if (!inDraft && inOriginal) {
            add(presetId, optionItemId, PresetItemUpdateType.Unlinked);
        } else if (inDraft && inOriginal && draftDefault !== originalDefault) {
            // Default toggled on/off for an already-linked product.
            add(presetId, optionItemId, draftDefault ? PresetItemUpdateType.Default : PresetItemUpdateType.Linked);
        }
    }

    return Array.from(byPreset.entries()).map(([presetId, items]) => ({ presetId, items }));
}

export interface MatrixProduct extends BaseProduct {
    productId: number;
    optionItemId: number;
    optionGroupId: number;
}

export interface MatrixData {
    optionGroupId: number;
    optionGroupName: string;
    optionGroupInternalNote?: string | null;
    rooms: RoomDto[];
    tags: ProjectTagDto[];
    products: MatrixProduct[];
}

interface Option {
    label: React.ReactNode;
    value: number;
}

export interface BuyerOptionPresetMatrixProps {
    presetGroup: OptionPresetGroupDto;
}

// Unified interface for both option groups and products
interface FlattenedMatrixItem {
    id: string;
    name: string;
    internalNote?: string | null;
    isGroup: boolean;
    rooms?: RoomDto[];
    tags?: ProjectTagDto[];
    subRows?: FlattenedMatrixItem[];
    originalProduct?: MatrixProduct;
    optionGroupId?: number; // Store the option group ID for products
    // Selection status is read from the local draft so edits show immediately, before saving.
    getPresetSelectionStatus: (presetId: number) => boolean;
    getIsDefault: (presetId: number) => boolean;
}

export const BuyerOptionPresetMatrix: React.FC<BuyerOptionPresetMatrixProps> = ({ presetGroup }) => {
    const presetGroupId = +presetGroup.id;
    const { data: matrix, isPending } = useOptionPresetMatrix(presetGroupId);
    const roomMap = useRoomMap(presetGroup.projectId);
    const saveMatrix = useSaveOptionPresetMatrix(presetGroupId);

    // Rooms load via a separate query; until it resolves matrixData is empty, so treat that as loading too
    // (otherwise the empty-state "Add option group" card flashes over a matrix that does have groups).
    const isLoading = isPending || !roomMap;

    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [moreInfoProductId, setMoreInfoProductId] = useState<number | null>(null);
    const [manuallyAddedGroupIds, setManuallyAddedGroupIds] = useState<number[]>([]);

    const presets = useMemo(() => matrix?.presets ?? [], [matrix?.presets]);
    const groups = useMemo(() => matrix?.groups ?? [], [matrix?.groups]);

    // The server-side linkage, used as the baseline for dirty detection and for diffing on save.
    const originalLinks = useMemo<LinkMap>(() => {
        const map: LinkMap = new Map();
        (matrix?.presetItems ?? []).forEach((pi) => map.set(linkKey(pi.optionPresetId, pi.optionItemId), pi.isDefault));
        return map;
    }, [matrix?.presetItems]);

    // The editable working copy. Adopt the server baseline on first load and whenever the server content
    // actually changes (e.g. after a save). A background refetch that returns identical content must NOT
    // clobber unsaved edits, so skip the reset when the incoming baseline matches the last one we synced.
    const [draft, setDraft] = useState<LinkMap>(new Map());
    const syncedLinksRef = useRef<LinkMap | null>(null);
    useEffect(() => {
        if (syncedLinksRef.current && linksEqual(syncedLinksRef.current, originalLinks)) {
            return;
        }
        syncedLinksRef.current = originalLinks;
        setDraft(new Map(originalLinks));
    }, [originalLinks]);

    // One diff drives both the dirty flag and the save payload, so they can never disagree.
    const pendingChanges = useMemo(() => computeChanges(draft, originalLinks), [draft, originalLinks]);
    const isDirty = pendingChanges.length > 0;

    // The matrix payload ships items only for groups that already have links (what renders on load).
    // When a group is added from the picker, lazily fetch just that group's items, mirroring the old
    // per-group load but only for the few groups you actually add. Groups the payload already covers
    // are skipped (a group that gains links comes back in the payload after saving).
    const matrixGroupIds = useMemo(() => new Set((matrix?.items ?? []).map((item) => item.optionGroupId)), [matrix?.items]);
    const groupsToLazyLoad = manuallyAddedGroupIds.filter((id) => !matrixGroupIds.has(id));
    const addedGroupItemQueries = useQueries({
        queries: groupsToLazyLoad.map((groupId) => ({
            queryKey: ['getOptionItemsByGroup', groupId],
            queryFn: () => getOptionItemsByGroup(groupId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
        })),
    });
    // useQueries returns a new array each render; key the merge off the loaded data, not that identity.
    const addedItemsSignature = addedGroupItemQueries.map((q) => q.dataUpdatedAt).join('|');
    const addedItems = useMemo(
        () => addedGroupItemQueries.flatMap((q) => q.data ?? []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [addedItemsSignature]
    );

    const itemsByGroup = useMemo(() => {
        const byGroup = groupBy(matrix?.items ?? [], (item) => item.optionGroupId);
        // Lazily-loaded items only fill groups the payload did not include, so a group present in both
        // (added, then linked and saved) does not render its products twice.
        Object.entries(groupBy(addedItems, (item) => item.optionGroupId)).forEach(([groupId, items]) => {
            if (!byGroup[groupId]) byGroup[groupId] = items;
        });
        return byGroup;
    }, [matrix?.items, addedItems]);

    // Structural view of the matrix (groups + their products). Independent of the draft selection state.
    const matrixData = useMemo<MatrixData[]>(() => {
        if (!roomMap) return [];
        return groups.map((group) => {
            const items = itemsByGroup[group.id] ?? [];
            const products: MatrixProduct[] = items.map((opt) => ({
                ...opt.product,
                productId: opt.product.id,
                optionItemId: opt.id,
                optionGroupId: group.id,
            }));

            return {
                optionGroupId: group.id,
                optionGroupName: group.name,
                optionGroupInternalNote: group.internalNote,
                rooms:
                    group.linkType === LinkingType.Room
                        ? group.linkedRooms.map((r) => roomMap.get(r.roomId)).filter((room): room is RoomDto => !!room)
                        : [],
                tags: group.tags ?? [],
                products,
            };
        });
    }, [groups, itemsByGroup, roomMap]);

    const optionItemIdsByGroup = useMemo(() => {
        const map = new Map<number, number[]>();
        matrixData.forEach((group) =>
            map.set(
                group.optionGroupId,
                group.products.map((p) => p.optionItemId)
            )
        );
        return map;
    }, [matrixData]);

    // --- Draft mutators (edit locally, persist on Save) ---
    // Link/unlink one or more products in a group, then re-establish exactly one default (backend rule).
    // Covers both a single product checkbox (one id) and the group "toggle all" checkbox (every id).
    const toggleLinks = useCallback(
        (presetId: number, optionItemIds: number[], groupOptionItemIds: number[], linked: boolean) => {
            setDraft((prev) => {
                const next = new Map(prev);
                optionItemIds.forEach((id) => {
                    const key = linkKey(presetId, id);
                    if (linked) {
                        if (!next.has(key)) next.set(key, false);
                    } else {
                        next.delete(key);
                    }
                });
                ensureSingleDefault(next, presetId, groupOptionItemIds);
                return next;
            });
        },
        []
    );

    const setProductDefault = useCallback((presetId: number, optionItemId: number, groupOptionItemIds: number[]) => {
        setDraft((prev) => {
            const next = new Map(prev);
            // Only one default per group per style: clear the flag on siblings (keep them linked).
            groupOptionItemIds.forEach((id) => {
                const key = linkKey(presetId, id);
                if (next.has(key)) next.set(key, false);
            });
            // Setting a default implies the product is linked.
            next.set(linkKey(presetId, optionItemId), true);
            return next;
        });
    }, []);

    const handleDiscard = useCallback(() => setDraft(new Map(originalLinks)), [originalLinks]);

    const handleSave = useCallback(async () => {
        if (pendingChanges.length === 0) return;
        try {
            await saveMatrix.mutateAsync(pendingChanges);
            // On success the matrix query is invalidated; the refreshed baseline resets the draft above.
            Toast.success(t`Changes saved`);
        } catch {
            Toast.error(t`Unable to save style products`);
        }
    }, [pendingChanges, saveMatrix]);

    // Groups that already had links when the matrix loaded stay visible for the whole editing session,
    // so unchecking the last product in a group does not make the row vanish mid-edit. It disappears
    // after saving, when the data reloads, matching the "only groups with selections" view.
    const initiallyVisibleGroupIds = useMemo(() => {
        const linkedItemIds = new Set<number>();
        originalLinks.forEach((_value, key) => linkedItemIds.add(Number(key.split(':')[1])));
        const groupIds = new Set<number>();
        matrixData.forEach((group) => {
            if (group.products.some((p) => linkedItemIds.has(p.optionItemId))) {
                groupIds.add(group.optionGroupId);
            }
        });
        return groupIds;
    }, [originalLinks, matrixData]);

    // Show a group if it has any linked product (in the current draft), was visible at load, or was manually added.
    const filteredMatrixData = useMemo(() => {
        return matrixData.filter((group) => {
            const hasAnyLink = group.products.some((product) =>
                presets.some((preset) => draft.has(linkKey(preset.id, product.optionItemId)))
            );
            return (
                hasAnyLink ||
                initiallyVisibleGroupIds.has(group.optionGroupId) ||
                manuallyAddedGroupIds.includes(group.optionGroupId)
            );
        });
    }, [matrixData, manuallyAddedGroupIds, draft, presets, initiallyVisibleGroupIds]);

    const flattenedData = useMemo(() => {
        const flattened: FlattenedMatrixItem[] = [];

        filteredMatrixData.forEach((group) => {
            const productItems: FlattenedMatrixItem[] = group.products.map((product) => ({
                id: `p${product.optionItemId}`,
                name: product.name,
                isGroup: false,
                originalProduct: product,
                optionGroupId: group.optionGroupId,
                getPresetSelectionStatus: (presetId: number) => draft.has(linkKey(presetId, product.optionItemId)),
                getIsDefault: (presetId: number) => draft.get(linkKey(presetId, product.optionItemId)) === true,
            }));

            const groupItem: FlattenedMatrixItem = {
                id: `g${group.optionGroupId}`,
                name: group.optionGroupName,
                internalNote: group.optionGroupInternalNote,
                isGroup: true,
                rooms: group.rooms,
                tags: group.tags,
                subRows: productItems,
                getPresetSelectionStatus: (presetId: number) =>
                    productItems.length > 0 && productItems.every((product) => product.getPresetSelectionStatus(presetId)),
                getIsDefault: () => false, // Groups don't have defaults
            };

            flattened.push(groupItem);
        });

        return orderBy(flattened, ['name'], ['asc']);
    }, [filteredMatrixData, draft]);

    // Get all unique preset IDs from the preset group to create dynamic columns
    const presetColumns = useMemo(() => {
        return presets.map((preset) => ({ id: preset.id, name: preset.name, description: preset.description }));
    }, [presets]);

    const columns = useMemo<ColumnDef<FlattenedMatrixItem, any>[]>(
        () => [
            {
                accessorKey: 'name',
                header: 'Name',
                cell: ({ row, getValue }: CellContext<FlattenedMatrixItem, string>) => {
                    const isGroup = row.original.isGroup;
                    const depthClass = `depth${Math.min(row.depth, 3)}`;
                    const typeClass = isGroup ? 'group' : 'product';

                    return (
                        <div className={`${theme.nameCell} ${theme[typeClass]} ${theme[depthClass]}`}>
                            {isGroup && (
                                <label className={theme.expandLabel}>
                                    <button
                                        type="button"
                                        aria-label="Toggle group"
                                        aria-expanded={row.getIsExpanded()}
                                        onClick={row.getToggleExpandedHandler()}
                                        className={`${theme.expandToggle} ${row.getIsExpanded() ? theme.expanded : ''}`}>
                                        <span className={theme.expandToggleArrow} />
                                    </button>
                                    <OptionGroupLabel name={row.original.name} internalNote={row.original.internalNote} />
                                    <div>
                                        <RoomPillList max={3} rooms={row.original.rooms || []} />
                                    </div>
                                    {(row.original.tags?.length ?? 0) > 0 && (
                                        <div>
                                            <Tags tags={row.original.tags ?? []} />
                                        </div>
                                    )}
                                </label>
                            )}
                            {!isGroup && (
                                <>
                                    <CatalogProductThumbnails
                                        catalog={ProductSourceCatalog.Project}
                                        images={row.original.originalProduct?.images || []}
                                        size={35}
                                    />
                                    {getValue()}

                                    <Button
                                        size="xs"
                                        onClick={() => setMoreInfoProductId(row.original.originalProduct?.productId || null)}
                                        variant="tertiary"
                                        icon={faInfoCircle}
                                    />
                                </>
                            )}
                        </div>
                    );
                },
            },

            // Dynamic columns for each preset
            ...presetColumns.map((preset) => ({
                id: `preset-${preset.id}`,
                header: () => (
                    <div title={preset.description || preset.name}>
                        <div>{preset.name}</div>
                        <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '2px' }}>Default • Include</div>
                    </div>
                ),
                cell: ({ row }: CellContext<FlattenedMatrixItem, unknown>) => {
                    const product = row.original.originalProduct;
                    const isSelected = row.original.getPresetSelectionStatus(preset.id);
                    const isDefault = row.original.getIsDefault(preset.id);
                    const isGroup = row.original.isGroup;

                    if (isGroup) {
                        const someSelected =
                            row.original.subRows !== undefined &&
                            row.original.subRows.some((x) => x.getPresetSelectionStatus(preset.id));
                        const isIndeterminate = !isSelected && someSelected;

                        return (
                            <div className={theme.presetCell}>
                                <Radio checked={false} disabled className={theme.hidden} />
                                {!presetGroup.legacyMode && (
                                    <Checkbox
                                        key={`${row.id}-${preset.id}-${isSelected}-${isIndeterminate}`}
                                        inputSize="md"
                                        indeterminate={isIndeterminate}
                                        checked={isSelected}
                                        onChange={() => {
                                            const subRows = row.original.subRows || [];
                                            const groupItemIds = subRows.map((x) => x.originalProduct!.optionItemId);
                                            toggleLinks(preset.id, groupItemIds, groupItemIds, !isSelected);
                                        }}
                                        title="Toggle all products in this group for this preset"
                                    />
                                )}
                            </div>
                        );
                    }

                    return (
                        <div className={theme.presetCell}>
                            <Radio
                                checked={isDefault}
                                onChange={() => {
                                    if (!product) return;
                                    setProductDefault(
                                        preset.id,
                                        product.optionItemId,
                                        optionItemIdsByGroup.get(product.optionGroupId) ?? []
                                    );
                                }}
                                title="Set as default for this style"
                            />
                            {!presetGroup.legacyMode && (
                                <Checkbox
                                    inputSize="md"
                                    checked={isSelected}
                                    onChange={() => {
                                        if (!product) return;
                                        toggleLinks(
                                            preset.id,
                                            [product.optionItemId],
                                            optionItemIdsByGroup.get(product.optionGroupId) ?? [],
                                            !isSelected
                                        );
                                    }}
                                    title="Include in this preset"
                                />
                            )}
                        </div>
                    );
                },
                size: 120,
            })),
        ],
        [presetColumns, presetGroup.legacyMode, optionItemIdsByGroup, setProductDefault, toggleLinks]
    );

    const handleFilterOption = (option: FilterOptionOption<Option>, searchText: string) => {
        const optionGroup = groups.find((x) => x.id === +option.value)!;
        return optionGroup.name.toLowerCase().includes(searchText.toLowerCase());
    };

    const availableGroupOptions = useMemo(
        () =>
            groups
                .filter((g) => !filteredMatrixData.some((md) => md.optionGroupId === g.id))
                .map<Option>((g) => ({
                    label: <OptionGroupDropdownItem group={g} />,
                    value: g.id,
                })),
        [groups, filteredMatrixData]
    );

    // Shared between the populated view and the empty state so the two stay in lockstep.
    const addOptionGroupCard = (
        <Card>
            <h3>Add option group</h3>
            <Select<Option>
                filterOption={handleFilterOption}
                placeholder="Select an option group to add..."
                options={availableGroupOptions}
                value={null}
                onChange={(newValue: any) => {
                    if (newValue) {
                        setManuallyAddedGroupIds((prev) => [...prev, newValue.value]);
                    }
                }}
                closeMenuOnSelect={false}
                isClearable
                maxMenuHeight={500}
            />
        </Card>
    );

    const table = useReactTable<FlattenedMatrixItem>({
        data: flattenedData,
        columns,
        state: { expanded },
        onExpandedChange: setExpanded,
        getSubRows: (row) => row.subRows,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getRowId: (row) => row.id,
    });

    const stickyContext = useContext(StickyContext);
    // Stick just below the page header + tab strip (currentOffset). We intentionally omit
    // stickyHeight: nothing sticky sits above the table inside this container, but a sibling
    // <ButtonBar sticky> from the styles list leaves a stale stickyHeight behind when it unmounts
    // (Sticky doesn't reset its reported height on unmount), which would otherwise push the header
    // down and reveal a row above it.
    const stickyHeaderTop = Math.max(0, stickyContext.currentOffset - 1);
    return (
        <div>
            <RouterPrompt when={isDirty} />

            {isLoading && <LoadingIndicator />}

            {filteredMatrixData.length > 0 ? (
                <>
                    <Banner type="info">
                        The style choice matrix lets you manage which products are available for each style and set the
                        default selections. Use the checkboxes to include or exclude products, and the radio buttons to set
                        the default choice. Click Save to apply your changes.
                    </Banner>

                    <Card className={theme.tableContainer}>
                        <table
                            className={theme.table}
                            style={{ '--matrix-sticky-top': `${stickyHeaderTop}px` } as React.CSSProperties}>
                            <thead>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <th key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {table.getRowModel().rows.map((row) => {
                                    const isGroupRow = row.original.isGroup;
                                    return (
                                        <tr key={row.id} className={isGroupRow ? 'group-row' : 'product-row'}>
                                            {row.getVisibleCells().map((cell) => (
                                                <td key={cell.id}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Card>
                    <div className={theme.addGroupCard}>{addOptionGroupCard}</div>
                </>
            ) : (
                <div className={theme.emptyState}>
                    {isLoading ? (
                        <LoadingIndicator />
                    ) : presets.length === 0 ? (
                        <Banner type="warning">
                            <p>No styles found in this group. Please add styles to see the matrix.</p>
                        </Banner>
                    ) : groups.length === 0 ? (
                        <Banner type="warning">
                            <p>
                                {presetGroup.type === OptionPresetGroupType.Room ? (
                                    <Trans>
                                        No option groups found for room styles. Please connect option groups to rooms to see
                                        the matrix.
                                    </Trans>
                                ) : (
                                    <Trans>
                                        No option groups found in the project. Please create option groups to see the matrix.
                                    </Trans>
                                )}
                            </p>
                        </Banner>
                    ) : (
                        addOptionGroupCard
                    )}
                </div>
            )}

            {moreInfoProductId !== null && (
                <ProductMoreInfoModal
                    isOpen={moreInfoProductId !== null}
                    product={
                        (matrixData
                            .find((group) => group.products.some((p) => p.productId === moreInfoProductId))
                            ?.products.find((p) => p.productId === moreInfoProductId) as BaseProduct) || null
                    }
                    toggleModal={() => setMoreInfoProductId(null)}
                />
            )}

            <PopupBar open={isDirty} className={theme.saveBar}>
                <Button variant="tertiary" onClick={handleDiscard} disabled={saveMatrix.isPending}>
                    <Trans>Discard</Trans>
                </Button>
                <Button variant="primary" onClick={handleSave} loading={saveMatrix.isPending}>
                    <Trans>Save changes</Trans>
                </Button>
            </PopupBar>
        </div>
    );
};
